import { useMemo } from 'react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import graphqlClient from '@/graphql/graphql-client';
import {
  toSystemSettingsRequest,
  useGetApiUsageLogs,
  useGetSystemSettings,
  type ApiUsageLogItem,
  type GetSystemSettingsResponse,
} from '@/graphql/queries/admin';
import {
  INSERT_SYSTEM_SETTING_MUTATION,
  UPDATE_SYSTEM_SETTING_MUTATION,
} from '@/graphql/mutations/admin';
import type { AnalyticsStats } from '../types/admin.types';
import { fetchOpenRouterModels, type TransformedModel } from '../services/openrouter.service';

const toAnalyticsStats = (items: Array<ApiUsageLogItem | null> | null | undefined): AnalyticsStats => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recentLogs = (items ?? []).filter((log): log is ApiUsageLogItem => {
    if (!log?.CreatedDate) return false;
    return new Date(log.CreatedDate) >= thirtyDaysAgo;
  });

  const totalRequests = recentLogs.length;
  const totalTokens = recentLogs.reduce((sum, log) => sum + (log.TotalTokens || 0), 0);
  const totalCost = recentLogs.reduce((sum, log) => sum + (log.Cost || 0), 0);
  const avgResponseTime =
    totalRequests > 0
      ? recentLogs.reduce((sum, log) => sum + (log.ResponseTime || 0), 0) / totalRequests
      : 0;
  const successCount = recentLogs.filter((log) => log.Success).length;
  const successRate = totalRequests > 0 ? (successCount / totalRequests) * 100 : 0;

  const templateMap = new Map<string, { requests: number; cost: number }>();
  for (const log of recentLogs) {
    const slug = log.TemplateSlug || 'unknown';
    const existing = templateMap.get(slug) || { requests: 0, cost: 0 };
    existing.requests++;
    existing.cost += log.Cost || 0;
    templateMap.set(slug, existing);
  }

  const dayMap = new Map<string, { requests: number; cost: number; tokens: number }>();
  for (const log of recentLogs) {
    const date = log.CreatedDate ? log.CreatedDate.split('T')[0] : 'unknown';
    const existing = dayMap.get(date) || { requests: 0, cost: 0, tokens: 0 };
    existing.requests++;
    existing.cost += log.Cost || 0;
    existing.tokens += log.TotalTokens || 0;
    dayMap.set(date, existing);
  }

  return {
    summary: { totalRequests, totalTokens, totalCost, avgResponseTime, successRate },
    byTemplate: Array.from(templateMap.entries()).map(([templateSlug, data]) => ({
      templateSlug,
      ...data,
    })),
    dailyTrends: Array.from(dayMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date)),
  };
};

export const useGetOpenRouterModels = (
  useCase?: 'documentGeneration' | 'formEnrichment' | 'dynamicForm',
  forceRefresh = false
) => {
  return useGlobalQuery<TransformedModel[]>({
    queryKey: ['openRouterModels', useCase, forceRefresh],
    queryFn: () => fetchOpenRouterModels(useCase, forceRefresh),
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetAnalyticsStats = () => {
  const query = useGetApiUsageLogs({ paging: { pageNo: 1, pageSize: 10000 } });
  const stats = useMemo(
    () => toAnalyticsStats(query.data?.getApiUsageLogss?.items),
    [query.data]
  );

  return {
    ...query,
    data: stats,
  };
};

export const useGetSettingByKey = (key: string) => {
  return useGetSystemSettings({
    where: { Key: { eq: key } },
    paging: { pageNo: 1, pageSize: 1 },
  });
};

export const useGetSettingsMap = () => {
  const query = useGetSystemSettings({ paging: { pageNo: 1, pageSize: 200 } });
  const settingsMap = useMemo(() => {
    const map: Record<string, string> = {};
    const items = query.data?.getSystemSettingss?.items || [];
    for (const setting of items) {
      if (setting.Key && setting.Value !== undefined) {
        map[setting.Key] = setting.Value;
      }
    }
    return map;
  }, [query.data]);

  return {
    ...query,
    data: settingsMap,
  };
};

export const useUpsertSetting = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: async (variables: { key: string; value: string; description?: string }) => {
      const request = toSystemSettingsRequest({
        where: { Key: { eq: variables.key } },
        paging: { pageNo: 1, pageSize: 1 },
      });
      const existing = await graphqlClient.request<GetSystemSettingsResponse>(
        request.document,
        request.variables
      );
      const item = existing?.getSystemSettingss?.items?.[0];
      if (item) {
        return graphqlClient.request(UPDATE_SYSTEM_SETTING_MUTATION, {
          ItemId: item.ItemId,
          Value: variables.value,
          ...(variables.description !== undefined && { Description: variables.description }),
        });
      }
      return graphqlClient.request(INSERT_SYSTEM_SETTING_MUTATION, {
        Key: variables.key,
        Value: variables.value,
        Description: variables.description ?? '',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to save setting' });
    },
  });
};
